// src/pages/SuperAdmin/Users/UserForm.jsx
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  phoneCountries,
  getDialByCountry,
  dialToCountry,
} from "../Venues/locationOptions";

import FormButton from "../../../components/form/FormButton";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";

import "./users.css";

const empty = {
  fullName: "",
  email: "",
  countryCode: "+91",
  country: "IN",
  mobileNumber: "",
  password: "",
};

const ccRegex = /^\+\d{1,4}$/;
const mobileRegex = /^\d{6,14}$/;

const getFirstErrorMessage = (errorsObj) => {
  const firstKey = Object.keys(errorsObj || {})[0];
  return firstKey ? errorsObj[firstKey] : "";
};

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const UserForm = ({
  mode = "create",
  initialData,
  onCancel,
  onSubmit,
  submitting = false,
}) => {
  const [form, setForm] = useState(initialData || empty);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      const cc = initialData.countryCode || "+91";
      const deducedCountry = initialData.country || dialToCountry[cc] || "IN";

      setForm({
        fullName: initialData.fullName || "",
        email: initialData.email || "",
        countryCode: cc,
        country: deducedCountry,
        mobileNumber: initialData.mobileNumber || "",
        password: "",
      });

      setErrors({});
      return;
    }

    setForm(empty);
    setErrors({});
  }, [initialData]);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));

    setErrors((prev) => {
      if (!prev[key]) return prev;

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const e = {};

    if (!form.fullName.trim()) {
      e.fullName = "Full name is required";
    }

    if (!form.email.trim()) {
      e.email = "Email is required";
    }

    if (!form.countryCode.trim()) {
      e.countryCode = "Country code is required";
    } else if (!ccRegex.test(form.countryCode.trim())) {
      e.countryCode = "Use country code format like +91";
    }

    if (!form.mobileNumber.trim()) {
      e.mobileNumber = "Mobile number is required";
    } else if (!mobileRegex.test(form.mobileNumber.trim())) {
      e.mobileNumber = "Mobile number must be 6–14 digits only";
    }

    if (mode === "create" && !form.password.trim()) {
      e.password = "Password is required";
    }

    setErrors(e);
    return e;
  };

  const submit = (ev) => {
    ev.preventDefault();

    if (submitting) return;

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      toast.error(
        getFirstErrorMessage(validationErrors) || "Please fix validation errors.",
      );
      return;
    }

    onSubmit?.({
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      countryCode: form.countryCode.trim(),
      mobileNumber: form.mobileNumber.trim(),
      country: form.country || undefined,
      ...(mode === "create" ? { password: form.password.trim() } : {}),
    });
  };

  return (
    <form className="users-form" onSubmit={submit} noValidate>
      <div className="users-form-grid">
        <FormField label="Full Name" error={errors.fullName}>
          <FormInput
            type="text"
            value={form.fullName}
            onChange={(e) => set("fullName", e.target.value)}
            placeholder="John Carter"
            required
          />
        </FormField>

        <FormField label="Email" error={errors.email}>
          <FormInput
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="john@example.com"
            required
          />
        </FormField>

        <FormField label="Country">
          <FormSelect
            value={form.country || "IN"}
            onChange={(e) => {
              const code = e.target.value;
              set("country", code);
              set("countryCode", getDialByCountry(code));
            }}
            required
          >
            {phoneCountries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.name} ({c.dial})
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField label="Country Code" error={errors.countryCode}>
          <FormInput
            type="text"
            value={form.countryCode}
            onChange={(e) => set("countryCode", e.target.value)}
            placeholder="+91"
            required
            readOnly
          />
        </FormField>

        <FormField label="Mobile (digits only)" error={errors.mobileNumber}>
          <FormInput
            type="tel"
            value={form.mobileNumber}
            onChange={(e) => set("mobileNumber", onlyDigits(e.target.value))}
            placeholder={
              form.countryCode === "+977"
                ? "9812345678"
                : form.countryCode === "+966"
                  ? "512345678"
                  : "9812345678"
            }
            required
          />
        </FormField>

        {mode === "create" && (
          <FormField label="Password" error={errors.password}>
            <FormInput
              type="password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder="••••••••"
              required
            />
          </FormField>
        )}
      </div>

      <div className="users-form-actions">
        <FormButton type="submit" variant="primary" disabled={submitting}>
          {submitting
            ? "Saving..."
            : mode === "create"
              ? "Create User"
              : "Save Changes"}
        </FormButton>

        <FormButton
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </FormButton>
      </div>
    </form>
  );
};

export default UserForm;